import type { WsCommand, WsEvent } from "@badorbad/shared";
import { supabase } from "./supabase";

const baseUrl = import.meta.env.VITE_BE_URL;
const wsUrl = baseUrl.replace(/^http/, "ws") + "/ws";

export interface WsMessage {
  event: WsEvent;
  payload: Record<string, unknown>;
  version: number;
}

export type WsListener = (msg: WsMessage) => void;
/** Fired whenever the socket drops, so the UI can stop accepting commands. */
export type WsCloseListener = () => void;

let socket: WebSocket | null = null;
let listener: WsListener | null = null;
let closeListener: WsCloseListener | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let active = false;

async function freshToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

function onMessage(raw: MessageEvent) {
  if (!listener) return;
  const msg: WsMessage = JSON.parse(
    typeof raw.data === "string" ? raw.data : raw.data.toString(),
  );
  listener(msg);
}

function scheduleReconnect() {
  closeListener?.();
  if (reconnectTimer || !active) return;
  reconnectTimer = setTimeout(async () => {
    reconnectTimer = null;
    if (!active) return;
    const token = await freshToken();
    if (token && active) openSocket(token);
  }, 2_000);
}

function openSocket(token: string) {
  if (socket) {
    socket.removeEventListener("close", scheduleReconnect);
    socket.close();
    socket = null;
  }
  const ws = new WebSocket(`${wsUrl}?token=${token}`);
  ws.addEventListener("message", onMessage);
  ws.addEventListener("close", scheduleReconnect);
  ws.addEventListener("error", () => ws.close());
  socket = ws;
}

export async function connect(): Promise<void> {
  const token = await freshToken();
  if (!token) return;
  disconnect();
  active = true;
  return new Promise<void>((resolve, reject) => {
    const ws = new WebSocket(`${wsUrl}?token=${token}`);
    ws.addEventListener("message", onMessage);
    ws.addEventListener("close", scheduleReconnect);
    ws.addEventListener("open", () => resolve(), { once: true });
    ws.addEventListener("error", () => {
      ws.close();
      reject(new Error("Connection failed"));
    }, { once: true });
    socket = ws;
  });
}

export function disconnect() {
  active = false;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (socket) {
    socket.removeEventListener("close", scheduleReconnect);
    socket.close();
    socket = null;
  }
}

/**
 * Returns whether the command actually went out. Callers that clear local state
 * on the strength of a command — leaving a room, above all — must not treat a
 * dropped frame as success: the server would still hold the player in the room
 * while the client believes it left.
 */
export function send(cmd: WsCommand, payload?: Record<string, unknown>): boolean {
  if (!socket || socket.readyState !== WebSocket.OPEN) return false;
  socket.send(JSON.stringify({ cmd, payload }));
  return true;
}

export function setListener(fn: WsListener | null) {
  listener = fn;
}

export function setCloseListener(fn: WsCloseListener | null) {
  closeListener = fn;
}

export function isConnected(): boolean {
  return socket !== null && socket.readyState === WebSocket.OPEN;
}
